import { useEffect, useRef, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../shared/ui/Button';
import styles from './VoiceConsentDialog.module.css';

interface VoiceConsentDialogProps {
  isPending: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}

// PARA-132 (AC-7, AC-9, AC-15) — согласие на отправку аудио внешнему
// STT-провайдеру. Тексты — черновик, требующий юридического утверждения перед
// релизом (Решение №8, AC-13a); пометка живёт в словарях (ключ
// consent.legalReviewNote) и проверяется тестом паритета локалей.
export function VoiceConsentDialog({
  isPending,
  onConfirm,
  onDismiss,
}: VoiceConsentDialogProps): JSX.Element {
  const { t } = useTranslation('voice');
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Фокус входит в модалку сразу: иначе screen reader продолжил бы читать
    // форму под ней, а Escape некому было бы поймать.
    dialogRef.current?.querySelector('button')?.focus();
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onDismiss();
      return;
    }
    if (event.key !== 'Tab') {
      return;
    }
    // Фокус-трап: за пределы модалки Tab не уводит.
    const focusable = [...(dialogRef.current?.querySelectorAll('button') ?? [])];
    if (focusable.length === 0) {
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    // Клавиатурный трап живёт на контейнере: события всплывают от любой
    // сфокусированной кнопки внутри модалки.
    <div className={styles.scrim} onKeyDown={handleKeyDown}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={t('consent.title')}
        data-testid="voice-consent-dialog"
      >
        <h2 className={styles.title}>{t('consent.title')}</h2>
        <p className={styles.message}>{t('consent.message')}</p>
        <p className={styles.note}>{t('consent.details')}</p>
        <div className={styles.actions}>
          <Button variant="ghost" fullWidth onClick={onDismiss} disabled={isPending}>
            {t('consent.dismiss')}
          </Button>
          <Button fullWidth onClick={onConfirm} disabled={isPending}>
            {t('consent.accept')}
          </Button>
        </div>
      </div>
    </div>
  );
}
